import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Megaphone, X, ArrowRight } from 'lucide-react';
import { getJson } from '../lib/fetchApi'; // fetch, not axios: this component is in the shared bundle

// Simple string hash so an edited banner message reappears even after a
// previous banner was dismissed.
const hash = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return String(h);
};

export const PromoBanner = () => {
  const [banner, setBanner] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let alive = true;
    getJson('/settings/banner')
      .then(({ data }) => {
        if (!alive) return;
        if (data?.enabled && data?.message) {
          const key = hash(data.message + (data.link_url || ''));
          if (localStorage.getItem('idb_banner_dismissed') === key) {
            setDismissed(true);
          }
          setBanner({ ...data, key });
        }
      })
      .catch(() => {});
    return () => { alive = false; };
    // Mount-only fetch by design: `getJson` is a stable module import and all other
    // referenced values are effect-locals, so there are no reactive deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!banner || dismissed) return null;

  const dismiss = () => {
    // Non-sensitive UI preference (hash of banner text) - localStorage is
    // intentional so the dismissal persists across visits. No tokens here.
    localStorage.setItem('idb_banner_dismissed', banner.key);
    setDismissed(true);
  };

  const isInternal = banner.link_url && banner.link_url.startsWith('/');

  return (
    <div className="relative bg-[#3E6F8E] text-white" data-testid="promo-banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 pr-12 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center">
        <Megaphone className="h-4 w-4 shrink-0 text-[#E7C9A6]" aria-hidden="true" />
        <p className="text-[13px] sm:text-sm font-medium leading-snug">{banner.message}</p>
        {banner.link_url && banner.link_label && (
          isInternal ? (
            <Link to={banner.link_url} data-testid="promo-banner-link" className="inline-flex items-center gap-1 text-[13px] sm:text-sm font-semibold underline underline-offset-4 decoration-[#E7C9A6] hover:text-[#E7C9A6] transition-colors max-md:min-h-11">
              {banner.link_label} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          ) : (
            <a href={banner.link_url} target="_blank" rel="noreferrer" data-testid="promo-banner-link" className="inline-flex items-center gap-1 text-[13px] sm:text-sm font-semibold underline underline-offset-4 decoration-[#E7C9A6] hover:text-[#E7C9A6] transition-colors max-md:min-h-11">
              {banner.link_label} <ArrowRight className="h-3.5 w-3.5" />
            </a>
          )
        )}
      </div>
      <button
        onClick={dismiss}
        aria-label="Dismiss announcement"
        data-testid="promo-banner-dismiss"
        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 max-md:h-11 max-md:w-11 max-md:p-0 max-md:inline-flex max-md:items-center max-md:justify-center rounded-md hover:bg-white/10 transition-colors"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="absolute bottom-0 inset-x-0 idb-gradient-hairline" />
    </div>
  );
};
